// Demonstrativo do acerto em PDF, feito no próprio aparelho (funciona sem
// sinal): viagens, créditos (comissão, diárias, agenciamentos…), vales,
// despesas pagas pelo colaborador, descontos, o saldo e a assinatura.
import { resultadoDoSaldo } from './acerto.js';
import { LARGURA, tracosDoCaminho } from './assinatura.js';
import { cpf as formatarCpf, data as formatarData, dinheiro, documento as formatarDocumento, placa as formatarPlaca } from './formato.js';

export const NOMES_CREDITO = {
  comissao: 'Comissão',
  valor_por_carga: 'Valor por carga',
  valor_por_viagem: 'Valor por viagem',
  diaria: 'Diárias',
  repasse_estadia: 'Repasse da estadia',
  agenciamento: 'Agenciamento',
  fixo_mensal: 'Fixo mensal',
  bonus: 'Bônus',
  outro: 'Outro crédito',
};
export const NOMES_DESCONTO = { avaria: 'Avaria', multa: 'Multa', outro: 'Outro desconto' };
const FORMAS = {
  dinheiro: 'em dinheiro', pix: 'por Pix', transferencia: 'por transferência', cartao: 'no cartão', boleto: 'por boleto', outro: '',
};

const soma = (lista) => lista.reduce((s, l) => s + Math.round(Number(l.valor) * 100), 0) / 100;

function horaLocal(iso, fuso) {
  if (!iso) return '';
  const quando = new Date(iso);
  const dia = new Intl.DateTimeFormat('pt-BR', { timeZone: fuso, day: '2-digit', month: '2-digit', year: 'numeric' }).format(quando);
  const hora = new Intl.DateTimeFormat('pt-BR', { timeZone: fuso, hour: '2-digit', minute: '2-digit' }).format(quando);
  return `${dia} às ${hora}`;
}

export function textoDaQuitacao(acerto) {
  if (acerto.deleted_at) return `Acerto desfeito${acerto.reaberto_motivo ? `: ${acerto.reaberto_motivo}` : ''}.`;
  switch (acerto.quitacao) {
    case 'paga': return `Pago em ${formatarData(acerto.quitado_em)}.`;
    case 'devolvida': return `Devolvido em ${formatarData(acerto.quitado_em)}.`;
    case 'transportada': return 'Saldo levado para o próximo acerto.';
    case 'zerado': return 'Acerto zerado.';
    default: return 'Ainda não quitado.';
  }
}

// Texto do demonstrativo (separado do desenho, para dar para testar).
export function textoDoDemonstrativo({ acerto, empresa, colaborador, viagens = [], veiculos = [], lancamentos = [], categorias = [], pagamento = null }) {
  const vivos = lancamentos.filter((l) => !l.deleted_at);
  const doTipo = (tipo) => vivos.filter((l) => l.tipo === tipo).sort((a, b) => (a.data < b.data ? -1 : a.data > b.data ? 1 : 0));
  const creditos = doTipo('credito');
  const vales = doTipo('vale');
  const despesas = doTipo('despesa');
  const descontos = doTipo('desconto');
  const nomeCat = (id) => categorias.find((c) => c.id === id)?.nome ?? 'Despesa';
  const doc = empresa.documento ? ` · ${empresa.tipo_pessoa === 'PF' ? 'CPF' : 'CNPJ'} ${formatarDocumento(empresa.documento)}` : '';
  const quem = colaborador?.apelido || colaborador?.nome || 'o colaborador';
  const secoes = [
    viagens.length > 0 && {
      titulo: 'Viagens',
      // Saída na primeira coluna; a viagem que estava em andamento na data do
      // acerto entra em parte (as cargas carregadas até essa data).
      linhas: viagens.map((v) => {
        const caminhao = veiculos.find((x) => x.id === v.veiculo_id);
        const placa = caminhao ? ` · caminhão ${formatarPlaca(caminhao.placa)}` : '';
        return [
          formatarData(v.data_saida),
          v.parcial ? `Parte da viagem: cargas até ${formatarData(acerto.data)}${placa}` : `Até ${formatarData(v.data_chegada)}${placa}`,
          '',
        ];
      }),
    },
    creditos.length > 0 && {
      titulo: 'Créditos (o que ele ganhou)',
      linhas: creditos.map((l) => [formatarData(l.data), l.descricao || NOMES_CREDITO[l.subtipo] || 'Crédito', dinheiro(l.valor)]),
      total: dinheiro(soma(creditos)),
    },
    vales.length > 0 && {
      titulo: 'Vales (adiantamentos)',
      linhas: vales.map((l) => [formatarData(l.data), `Vale${l.forma && FORMAS[l.forma] ? ` ${FORMAS[l.forma]}` : ''}${l.descricao ? ` · ${l.descricao}` : ''}`, dinheiro(l.valor)]),
      total: dinheiro(soma(vales)),
    },
    despesas.length > 0 && {
      titulo: 'Despesas pagas por ele',
      linhas: despesas.map((l) => [formatarData(l.data), [nomeCat(l.categoria_id), l.fornecedor].filter(Boolean).join(' · '), dinheiro(l.valor)]),
      total: dinheiro(soma(despesas)),
    },
    descontos.length > 0 && {
      titulo: 'Descontos',
      linhas: descontos.map((l) => [formatarData(l.data), l.descricao || NOMES_DESCONTO[l.subtipo] || 'Desconto', dinheiro(l.valor)]),
      total: dinheiro(soma(descontos)),
    },
  ].filter(Boolean);

  const resumo = [
    Number(acerto.saldo_anterior) !== 0 && ['Saldo do acerto anterior', dinheiro(acerto.saldo_anterior)],
    ['(+) Vales', dinheiro(acerto.total_vales)],
    ['(-) Despesas pagas por ele', dinheiro(acerto.total_despesas)],
    ['(-) Créditos', dinheiro(acerto.total_creditos)],
    ['(+) Descontos', dinheiro(acerto.total_descontos)],
    ['(=) Saldo', dinheiro(acerto.saldo)],
  ].filter(Boolean);

  let quitacao = textoDaQuitacao(acerto);
  if (pagamento && acerto.quitacao === 'paga') quitacao = `Pago em ${formatarData(pagamento.data)}${FORMAS[pagamento.forma] ? ` ${FORMAS[pagamento.forma]}` : ''}.`;
  if (pagamento && acerto.quitacao === 'devolvida') quitacao = `Devolvido em ${formatarData(pagamento.data)}${FORMAS[pagamento.forma] ? ` ${FORMAS[pagamento.forma]}` : ''}.`;

  return {
    titulo: `DEMONSTRATIVO DE ACERTO Nº ${acerto.numero}/${acerto.ano}`,
    empresa: `${empresa.nome}${doc}`,
    colaborador: `${colaborador?.nome ?? ''}${colaborador?.cpf ? ` · CPF ${formatarCpf(colaborador.cpf)}` : ''}`,
    periodo: acerto.periodo_de && acerto.periodo_de !== acerto.data
      ? `Período: ${formatarData(acerto.periodo_de)} a ${formatarData(acerto.data)}`
      : `Data: ${formatarData(acerto.data)}`,
    secoes,
    resumo,
    resultado: resultadoDoSaldo(Number(acerto.saldo), quem),
    quitacao,
    observacao: acerto.observacao || null,
    nome: colaborador?.nome ?? '',
    documento: colaborador?.cpf ? `CPF ${formatarCpf(colaborador.cpf)}` : '',
    declaracao: 'Confiro e concordo com os valores deste acerto.',
    assinado: acerto.assinatura
      ? `Assinado ${acerto.assinado_via === 'link' ? 'pelo link, no celular do colaborador,' : 'na tela do celular'} em ${horaLocal(acerto.assinado_em, empresa.fuso_horario || 'America/Sao_Paulo')}.`
      : 'Sem assinatura.',
  };
}

export function nomeDoArquivoDoAcerto({ acerto, colaborador }) {
  const pessoa = (colaborador?.apelido || colaborador?.nome || 'colaborador')
    .normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase();
  return `acerto-${acerto.numero}-${acerto.ano}-${pessoa}.pdf`;
}

// Monta o PDF e devolve um Blob. Quebra a página quando precisa.
export async function demonstrativoEmPdf(dados) {
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const t = desenharDemonstrativo(pdf, dados);
  rodape(pdf, t.titulo.toLowerCase().replace('demonstrativo de acerto', 'acerto'));
  return pdf.output('blob');
}

// Rodapé de todas as páginas: "Emitido pelo HSA Frota · <o que é> · página 1 de 3".
export function rodape(pdf, oQue) {
  const paginas = pdf.getNumberOfPages();
  for (let i = 1; i <= paginas; i++) {
    pdf.setPage(i);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(90);
    pdf.text(`Emitido pelo HSA Frota · ${oQue} · página ${i} de ${paginas}`, 18, 290);
  }
}

// Desenha o demonstrativo de um acerto a partir do topo da página atual
// (que precisa estar em branco). Devolve o texto usado.
export function desenharDemonstrativo(pdf, dados) {
  const t = textoDoDemonstrativo(dados);
  const margem = 18;
  const direita = 210 - margem;
  const fim = 280;
  let y = 24;
  pdf.setTextColor(0);

  const novaPagina = () => {
    pdf.addPage();
    y = 20;
  };
  const cabe = (altura) => {
    if (y + altura > fim) novaPagina();
  };

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(15);
  pdf.text(t.titulo, margem, y);
  y += 7;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.text(t.empresa, margem, y);
  y += 5;
  pdf.setFont('helvetica', 'bold');
  pdf.text(`Colaborador: ${t.colaborador}`, margem, y);
  pdf.setFont('helvetica', 'normal');
  y += 5;
  pdf.text(t.periodo, margem, y);
  y += 4;
  pdf.setLineWidth(0.3);
  pdf.line(margem, y, direita, y);
  y += 7;

  for (const secao of t.secoes) {
    cabe(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.text(secao.titulo, margem, y);
    y += 5.5;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9.5);
    for (const [quando, descricao, valor] of secao.linhas) {
      const linhas = pdf.splitTextToSize(descricao, direita - margem - 26 - 30);
      cabe(linhas.length * 4.2 + 1);
      pdf.text(quando, margem, y);
      pdf.text(linhas, margem + 26, y);
      if (valor) pdf.text(valor, direita, y, { align: 'right' });
      y += linhas.length * 4.2 + 0.8;
    }
    if (secao.total) {
      cabe(6);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Total', margem + 26, y);
      pdf.text(secao.total, direita, y, { align: 'right' });
      pdf.setFont('helvetica', 'normal');
      y += 5;
    }
    y += 3;
  }

  // Resumo
  cabe(10 + t.resumo.length * 5.5 + 16);
  pdf.setLineWidth(0.3);
  pdf.line(margem, y, direita, y);
  y += 6;
  pdf.setFontSize(10.5);
  for (const [rotulo, valor] of t.resumo) {
    const final = rotulo.startsWith('(=)');
    pdf.setFont('helvetica', final ? 'bold' : 'normal');
    pdf.text(rotulo, margem + 60, y);
    pdf.text(valor, direita, y, { align: 'right' });
    y += 5.5;
  }
  y += 2;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.text(t.resultado, margem, y);
  y += 5.5;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.text(t.quitacao, margem, y);
  y += 5;
  if (t.observacao) {
    const linhas = pdf.splitTextToSize(`Observação: ${t.observacao}`, direita - margem);
    cabe(linhas.length * 4.5);
    pdf.text(linhas, margem, y);
    y += linhas.length * 4.5;
  }

  // Assinatura
  cabe(52);
  y += 6;
  pdf.text(t.declaracao, margem, y);
  const linhaY = y + 32;
  const caixa = { x: 105 - 40, y: linhaY - 26, l: 80 };
  if (dados.acerto.assinatura) {
    pdf.setLineWidth(0.5);
    pdf.setLineCap('round');
    pdf.setLineJoin('round');
    const k = caixa.l / LARGURA;
    for (const traco of tracosDoCaminho(dados.acerto.assinatura)) {
      const pontos = traco.length === 1 ? [traco[0], [traco[0][0] + 0.5, traco[0][1]]] : traco;
      for (let i = 1; i < pontos.length; i++) {
        pdf.line(caixa.x + pontos[i - 1][0] * k, caixa.y + pontos[i - 1][1] * k, caixa.x + pontos[i][0] * k, caixa.y + pontos[i][1] * k);
      }
    }
  }
  pdf.setLineWidth(0.3);
  pdf.line(105 - 55, linhaY, 105 + 55, linhaY);
  pdf.setFontSize(10.5);
  pdf.setFont('helvetica', 'bold');
  pdf.text(t.nome, 105, linhaY + 5.5, { align: 'center' });
  pdf.setFont('helvetica', 'normal');
  if (t.documento) pdf.text(t.documento, 105, linhaY + 10.5, { align: 'center' });
  pdf.setFontSize(8.5);
  pdf.setTextColor(90);
  pdf.text(t.assinado, 105, linhaY + (t.documento ? 15.5 : 10.5), { align: 'center' });
  pdf.setTextColor(0);
  return t;
}
